import { useState } from 'react'
import { useDrop, useDrag, useFlag, UseDropProps } from '@sketch/modules-common'

export interface DragAndDroppedShareIds {
  draggedShareId: string
  droppedShareId: string
}

export interface DragAndDroppedProjectToShareIds {
  draggedProjectId: string
  droppedShareId: string
}

interface UseShareListItemDragDropProps
  extends Pick<
    UseDropProps,
    'onShareDragOver' | 'onProjectDragOver' | 'onUnknownDragOver'
  > {
  disabled: boolean
  shareId: string
  onShareDropped?: (args: DragAndDroppedShareIds) => void
  onProjectDropped?: (args: DragAndDroppedProjectToShareIds) => void
}

export const useShareListItemDragDrop = ({
  disabled,
  shareId: droppedShareId,
  onShareDropped,
  onProjectDropped,
}: UseShareListItemDragDropProps) => {
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)

  const isNestedProjectsOn = useFlag('nested-projects')

  const dropEvents = useDrop({
    onShareDropped: ({ shareId: draggedShareId }) => {
      onShareDropped?.({ draggedShareId, droppedShareId })
    },
    onProjectDropped: ({ projectId: draggedProjectId }) => {
      onProjectDropped?.({ draggedProjectId, droppedShareId })
    },
    onProjectDragOver: () => {
      setIsDroppableAreaEnabled(true)
    },
    onShareDragOver: () => {
      setIsDroppableAreaEnabled(true)
    },
    onUnknownDragOver: () => {
      setIsDroppableAreaEnabled(false)
    },
  })

  const dragEvents = useDrag({
    type: 'share',
    identifier: droppedShareId,
    ...(isNestedProjectsOn
      ? {
          customDragImage: true,
          customDragImageStyles: {
            transform: 'scale(0.8)',
          },
        }
      : {}),
  })

  return {
    draggable: !disabled,
    ...dragEvents,
    ...dropEvents,
    isDroppableAreaEnabled,
  }
}
